'use client';

import React, { useState } from 'react';
import { Flame, Heart, Users, Skull } from 'lucide-react';
import { toast } from 'sonner';

const tones = [
  {
    id: 'tone-provokativ',
    value: 'Provokativ',
    icon: <Flame size={20} />,
    label: 'Provokativ',
    desc: 'Direkt, reibend, polarisierend. Du sagst, was andere nicht aussprechen. Keine Weichspüler.',
    example: '"Die meisten LinkedIn-Posts sind Selbstgespräche. Niemand interessiert das."',
  },
  {
    id: 'tone-authentisch',
    value: 'Authentisch',
    icon: <Heart size={20} />,
    label: 'Authentisch',
    desc: 'Persönlich, ehrlich, nahbar. Echte Geschichten statt glatter Oberflächen.',
    example: '"Ich erinnere mich noch genau an den Tag, als wir fast alles verloren hätten."',
  },
  {
    id: 'tone-human',
    value: 'Human-Centric',
    icon: <Users size={20} />,
    label: 'Human-Centric',
    desc: 'Empathisch, lösungsorientiert, auf Augenhöhe. Der Mensch steht im Mittelpunkt.',
    example: '"Was brauchst du wirklich, um morgen besser zu starten als heute?"',
  },
  {
    id: 'tone-punk',
    value: 'Business-Punk',
    icon: <Skull size={20} />,
    label: 'Business-Punk',
    desc: 'Rebellisch, antikonform, mit Substanz. Gegen den Strom, aber mit Strategie.',
    example: '"Vergiss den Businessplan. Bau zuerst das Ding. Erkläre es danach."',
  },
];

export default function BrandVaultTone() {
  const [selected, setSelected] = useState('Business-Punk');

  const handleSave = () => {
    // TODO: Backend — PATCH /api/brand-vault/tone with { tone: selected }
    toast?.success(`Tonalität gesetzt: ${selected}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, value: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelected(value);
    }
  };

  return (
    <div className="punk-card p-5">
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-border">
        <div>
          <h2 className="text-sm font-700 text-white uppercase tracking-widest" id="tone-heading">Tone of Voice</h2>
          <p className="text-xs text-muted mt-1" id="tone-desc">Wähle den Grundton deiner KI-generierten Inhalte</p>
        </div>
        <span
          className="status-badge bg-accent/10 text-accent border border-accent/30 mono"
          role="status"
          aria-label={`Aktiver Ton: ${selected}`}
          aria-live="polite"
        >
          Aktiv: {selected}
        </span>
      </div>
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
        role="radiogroup"
        aria-labelledby="tone-heading"
        aria-describedby="tone-desc"
      >
        {tones?.map((tone) => {
          const isSelected = selected === tone?.value;
          return (
            <button
              key={tone?.id}
              onClick={() => setSelected(tone?.value)}
              onKeyDown={(e) => handleKeyDown(e, tone?.value)}
              className={`text-left p-4 rounded transition-all duration-150 border focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-background ${
                isSelected
                  ? 'border-accent bg-accent/8 neon-glow' :'border-border bg-surface hover:border-border-2 hover:bg-surface-2'
              }`}
              role="radio"
              aria-checked={isSelected}
              aria-label={`Ton: ${tone?.label} — ${tone?.desc}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className={isSelected ? 'text-accent' : 'text-muted'} aria-hidden="true">
                  {tone?.icon}
                </span>
                <span className={`font-700 text-sm ${isSelected ? 'text-accent' : 'text-white'}`}>
                  {tone?.label}
                </span>
                {isSelected && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-accent" aria-hidden="true" />
                )}
              </div>
              <p className="text-xs text-muted leading-relaxed mb-3">{tone?.desc}</p>
              <div className="border-l-2 border-accent/30 pl-3">
                <p className="text-xs text-muted-2 italic" aria-label={`Beispiel: ${tone?.example}`}>{tone?.example}</p>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={handleSave}
          className="btn-primary text-xs focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-background"
          aria-label={`Tonalität "${selected}" speichern`}
        >
          Tonalität speichern
        </button>
      </div>
    </div>
  );
}